// components/nav.jsx
import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  NavLink,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
  Collapse,
  ListSubheader,
  Menu,
  MenuItem,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';

import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';


import { UserContext } from './userContext';
import { dashboardPathForUser, isMarketingPath, isPubliclyAccessiblePath } from './public/publicRoutes';
import { PUBLIC_NAVIGATION } from './public/publicSiteRegistry.mjs';

const BRAND_BLUE = '#4b9ef1';

// Keep the authenticated application header on the single TaskerSys blue.
// White remains the foreground; active/open states use only very light white tints.
const NAV_TEXT = '#fff';
const NAV_HOVER_BG = 'rgba(255,255,255,0.06)';
const NAV_OPEN_BG = 'rgba(255,255,255,0.075)';
const NAV_ACTIVE_BG = 'rgba(255,255,255,0.055)';
const NAV_ACTIVE_LINE = 'rgba(255,255,255,0.50)';
const NAV_TEXT_SHADOW = '0 1px 2px rgba(15,23,42,0.22)';

/* ============================================================
   Constants
   ============================================================ */
const GM_ROUTES = [
  { path: '/ums/registration-form', label: 'Registration Forms' },
  { path: '/ums/general-manager-dashboard', label: 'GM Dashboard' },
  { path: '/ums/general-manager-settings', label: 'GM Settings' },
  { path: '/ums/general-manager-tasker-performance', label: 'Tasker Dashboard' },
  { path: '/ums/general-manager-services', label: 'Set Up Services' },
  { path: '/ums/general-manager-gallery', label: 'Set up gallery' },
  { path: '/ums/admin-panel', label: 'Admin Panel', adminOnly: true },
  { path: '/ums/admin-analytics', label: 'Analytics', adminOnly: true },
  { path: '/ums/admin-azure-cleaner', label: 'Azure Cleaner', adminOnly: true },
  { path: '/ums/admin-receipts', label: 'Receipts Dashboard', adminOnly: true },
];

const REFRESH_PATHS = [
  '/ums/registration-form',
  '/ums/general-manager-settings',
  '/ums/general-manager-dashboard',
  '/ums/general-manager-tasker-performance',
  '/ums/general-manager-services',
  '/dashboard',
  '/ums/general-manager-gallery',
  '/ums/admin-panel',
  '/ums/admin-analytics',
  '/ums/admin-azure-cleaner',
  '/ums/admin-receipts',
  '/ums/tasker-receipts',
];

/* Session nav history */
const NAV_HISTORY_KEY = 'navHistory.v1';
function readHistory() {
  try {
    const raw = sessionStorage.getItem(NAV_HISTORY_KEY);
    if (!raw) return { entries: [], index: -1 };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.entries) || typeof parsed.index !== 'number') {
      return { entries: [], index: -1 };
    }
    return parsed;
  } catch {
    return { entries: [], index: -1 };
  }
}
function writeHistory(hist) {
  try { sessionStorage.setItem(NAV_HISTORY_KEY, JSON.stringify(hist)); } catch {}
}

function normalizeRoutePath(value = '/') {
  const pathOnly = String(value || '/').split('?')[0].split('#')[0];
  if (!pathOnly || pathOnly === '/') return '/';
  return pathOnly.replace(/\/+$/, '') || '/';
}

function normalizeSearchParams(value = '') {
  return Array.from(new URLSearchParams(String(value || '').replace(/^\?/, '')).entries())
    .sort(([aKey, aValue], [bKey, bValue]) =>
      `${aKey}=${aValue}`.localeCompare(`${bKey}=${bValue}`)
    )
    .map(([key, itemValue]) => `${key}=${itemValue}`)
    .join('&');
}

function routeMatches(target, pathname, search, querySensitiveBasePaths = new Set()) {
  const rawTarget = String(target || '/');
  const questionIndex = rawTarget.indexOf('?');
  const targetPath = normalizeRoutePath(
    questionIndex >= 0 ? rawTarget.slice(0, questionIndex) : rawTarget
  );
  const targetSearch = questionIndex >= 0 ? rawTarget.slice(questionIndex + 1) : '';
  const currentPath = normalizeRoutePath(pathname);

  if (targetSearch) {
    return (
      currentPath === targetPath &&
      normalizeSearchParams(search) === normalizeSearchParams(targetSearch)
    );
  }

  if (targetPath === '/') return currentPath === '/';

  if (
    currentPath === targetPath &&
    search &&
    querySensitiveBasePaths.has(targetPath)
  ) {
    return false;
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

function getEntryItems(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const directItems = Array.isArray(entry.items) ? entry.items : [];
  const sectionItems = Array.isArray(entry.sections)
    ? entry.sections.flatMap((section) =>
        Array.isArray(section?.items) ? section.items : []
      )
    : [];
  return [...directItems, ...sectionItems];
}

/* ============================================================
   Link Button
   ============================================================ */
function LinkButton({ to, label, icon, sx, isActive, compact }) {
  const needsFull = REFRESH_PATHS.includes(to);

  const content = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 0 : 0.7 }}>
      {icon}
      {!compact && (
        <Box
          component="span"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 240,
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );

  const Btn = (
    <Button
      component={needsFull ? 'a' : NavLink}
      href={needsFull ? to : undefined}
      to={needsFull ? undefined : to}
      sx={sx}
      aria-current={isActive ? 'page' : undefined}
    >
      {content}
    </Button>
  );

  return (
    <Tooltip title={compact ? label : ''} arrow disableHoverListener={!compact}>
      <span>{Btn}</span>
    </Tooltip>
  );
}

/* ============================================================
   Desktop Dropdown (Menu with optional sections)
   ============================================================ */
function DropdownMenu({
  id,
  buttonSx,
  label,
  icon,
  items,
  sections,
  compact,
  active = false,
  isItemActive = () => false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const btnRef = useRef(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    btnRef.current?.focus();
  };

  const contentSections = useMemo(() => {
    if (sections && sections.length) return sections;
    if (items && items.length) return [{ label: null, items }];
    return [];
  }, [sections, items]);

  return (
    <>
      <Tooltip title={compact ? label : ''} arrow disableHoverListener={!compact}>
        <Button
          ref={btnRef}
          id={`${id}-button`}
          aria-controls={open ? `${id}-menu` : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
          data-active={active ? 'true' : undefined}
          sx={{
            ...buttonSx,
            ...(open && {
              backgroundColor: NAV_OPEN_BG,
              boxShadow: active
                ? `inset 0 -2px 0 ${NAV_ACTIVE_LINE}`
                : 'none',
            }),
          }}
          endIcon={!compact ? <KeyboardArrowDownRoundedIcon /> : null}
        >
          {icon}
          {!compact && (
            <Box component="span" sx={{ ml: 0.7, whiteSpace: 'nowrap' }}>
              {label}
            </Box>
          )}
        </Button>
      </Tooltip>

      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': `${id}-button`, dense: true, disablePadding: true }}
        elevation={1}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            borderRadius: 2,
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
            overflow: 'hidden',
          },
        }}
        sx={{ zIndex: 2000 }}
      >
        {contentSections.map((section, sectionIndex) => (
          <Box key={section.label || sectionIndex} sx={{ minWidth: 220 }}>
            {section.label && (
              <ListSubheader
                disableSticky
                sx={{
                  lineHeight: 1.4,
                  py: 0.75,
                  px: 2,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: '#4b9ef1',
                  backgroundColor: 'rgba(75,158,241,0.08)',
                }}
              >
                {section.label}
              </ListSubheader>
            )}

            {section.items?.map((it) => {
              const needsFull = REFRESH_PATHS.includes(it.path);
              const Comp = needsFull ? 'a' : RouterLink;
              const props = needsFull ? { href: it.path } : { to: it.path };
              const itemActive = isItemActive(it.path);
              return (
                <MenuItem
                  key={it.path}
                  component={Comp}
                  {...props}
                  selected={itemActive}
                  aria-current={itemActive ? 'page' : undefined}
                  onClick={() => {
                    queueMicrotask(() => handleClose());
                  }}
                  sx={{
                    px: 2,
                    py: 0.9,
                    minHeight: 38,
                    borderLeft: '2px solid transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(75,158,241,0.07)',
                      borderLeftColor: 'rgba(75,158,241,0.58)',
                      fontWeight: 650,
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(75,158,241,0.10)',
                    },
                  }}
                >
                  {it.label}
                </MenuItem>
              );
            })}

            {sectionIndex < contentSections.length - 1 && <Divider sx={{ my: 0.5 }} />}
          </Box>
        ))}
      </Menu>
    </>
  );
}


/* ============================================================
   Main Nav
   ============================================================ */
const Nav = () => {
  const { user, logoutUser } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const compact = useMediaQuery('(max-width:1400px)');
  const ultraCompact = useMediaQuery('(max-width:1100px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ GMPanel: false });
  const [openSubGroups, setOpenSubGroups] = useState({});

  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const queryClient = useQueryClient();

  const userType = useMemo(
    () => (user && user.usertype ? String(user.usertype).toLowerCase() : null),
    [user]
  );
  const hasActiveSubscription = useMemo(
    () => Array.isArray(user?.subscriptions) && user.subscriptions.some((s) => s.status === 'active'),
    [user]
  );

  const gmRoutesFiltered = useMemo(
    () => GM_ROUTES.filter((r) => !r.adminOnly || userType === 'admin'),
    [userType]
  );

  const dashboardPath = useMemo(
    () => (userType === 'general manager' ? '/ums/general-manager-dashboard' : '/dashboard'),
    [userType]
  );

  const isPublicShell = isMarketingPath(location.pathname);

  useEffect(() => {
    const current = `${location.pathname}${location.search || ''}`;
    let hist = readHistory();
    if (!hist.entries.length || hist.index < 0 || hist.index >= hist.entries.length) {
      hist = { entries: [current], index: 0 };
    } else if (hist.entries[hist.index] !== current) {
      hist.entries = hist.entries.slice(0, hist.index + 1);
      if (hist.entries[hist.entries.length - 1] !== current) {
        hist.entries.push(current);
        hist.index = hist.entries.length - 1;
      }
    }
    writeHistory(hist);
    setCanGoBack(hist.index > 0);
    setCanGoForward(hist.index < hist.entries.length - 1);
  }, [location.key, location.pathname, location.search]);

  const handleBack = useCallback(() => {
    const hist = readHistory();
    if (hist.index > 0) {
      const target = hist.entries[hist.index - 1];
      hist.index -= 1;
      writeHistory(hist);
      navigate(target, { replace: true });
      try { queryClient.cancelQueries(); } catch {}
      setCanGoBack(hist.index > 0);
      setCanGoForward(hist.index < hist.entries.length - 1);
    } else {
      if ((window.history?.length || 0) > 1) navigate(-1);
      else navigate(dashboardPath, { replace: true });
    }
  }, [navigate, dashboardPath, queryClient]);

  const handleForward = useCallback(() => {
    const hist = readHistory();
    if (hist.index < hist.entries.length - 1) {
      const target = hist.entries[hist.index + 1];
      hist.index += 1;
      writeHistory(hist);
      navigate(target, { replace: true });
      try { queryClient.cancelQueries(); } catch {}
      setCanGoBack(hist.index > 0);
      setCanGoForward(hist.index < hist.entries.length - 1);
    }
  }, [navigate, queryClient]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if ((e.altKey && e.key === 'ArrowLeft') || (e.metaKey && e.key === '[')) {
        e.preventDefault(); handleBack(); return;
      }
      if ((e.altKey && e.key === 'ArrowRight') || (e.metaKey && e.key === ']')) {
        e.preventDefault(); handleForward();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleBack, handleForward]);

  /* ------- Role → Menus map (with sections) ------- */
  const roleMenus = useMemo(() => {
    const shared = [
      {
        type: 'link',
        key: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardRoundedIcon fontSize="small" />,
        path:'/dashboard',
      },
      {
        type: 'link',
        key: 'partnerHub',
        label: 'Partner Hub',
        icon: <StorefrontRoundedIcon fontSize="small" />,
        path: '/marketplace',
        showFor: ['general manager', 'manager', 'admin', 'tasker', 'client'],
      },
    ];

    const canSeePartner = (role) =>
      shared[1].showFor.includes(role || '');

    const base = shared.filter((item, idx) => idx === 0 || canSeePartner(userType));

    if (!userType) return base;

    /* ===== TASKER ===== */
    if (userType === 'tasker') {
      return [
        ...base,
        {
          type: 'menu',
          key: 'work',
          label: 'Work',
          icon: <AccessTimeRoundedIcon fontSize="small" />,
          items: [
            {label: 'Generate quote', path: '/generateQuote' },
            { label: 'Create Tickets', path: '/manual-bulk-tickets' },
            { label: 'Timesheet', path: '/timesheetSummary' },
            { label: 'Schedule', path: '/implementer-schedule' },
          ],
        },
        {
          type: 'menu',
          key: 'financials',
          label: 'Financials',
          icon: <RequestQuoteRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Billing & Income',
              items: [
                { label: 'Invoice Generator', path: '/tasker-invoice-generator' },
                { label: 'Invoices', path: '/tasker-invoices' },
                { label: 'Accounts Receivable', path: '/tasker-receivable-ledger' },
              ],
            },
            {
              label: 'Payroll',
              items: [
                { label: 'Payroll Generator', path: '/payroll-generator' },
                { label: 'Payrolls', path: '/payrolls' },
              ],
            },
            {
              label: 'Expenses & Payables',
              items: [
                { label: 'Business Expenses', path: '/bookkeeping/business-receipts' },
                { label: 'External Payables', path: '/bookkeeping/external-payables' },
                { label: 'Accounts Payable', path: '/payable-ledger' },
                { label: 'Receipts', path: '/ums/tasker-receipts' },
              ],
            },
            {
              label: 'Banking & Reconciliation',
              items: [
                { label: 'Bank Accounts', path: '/bookkeeping/bank-accounts' },
                { label: 'Bank Ledger', path: '/bookkeeping/bank-ledger' },
                { label: 'Bank Reconciliation', path: '/tools/bank-reconciliation' },
                { label: 'Receipt Reconciliation', path: '/tools/receipt-reconciliation' },
              ],
            },
            {
              label: 'Accounting',
              items: [
                { label: 'Chart of Accounts', path: '/accounting/coa' },
                { label: 'Journal Entries', path: '/accounting/journal-entries' },
                { label: 'Category Ledger', path: '/accounting/categories' },
                { label: 'Posting Simulator', path: '/accounting/posting' },
                { label: 'Open/Close accounts', path: '/accounting/open-close-balances' },
                { label: 'Journal explorer', path: '/accounting/journal-explorer' },
                { label: 'Trial Balance', path: '/accounting/trial-balance' },
                { label: 'Profit & Loss', path: '/accounting/pl' },
                { label: 'Balance Sheet', path: '/accounting/balance-sheet' },
                { label: 'Financial Reports', path: '/accounting/financial-reports' },
                { label: 'Accounting Settings', path: '/accounting/tasker-accounting-setting' },
              ],
            },
          ],
        },
        {
          type: 'menu',
          key: 'insightsTools',
          label: 'Insights & Tools',
          icon: <AssessmentRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Analytics',
              items: [
                { label: 'Gross P&L per Project', path: '/pnl' },
                { label: 'Cash planning', path: '/expected-expenses' },
                { label: 'Implementer expenses', path: '/imp-expenses' },
                { label: 'Materials expenses', path: '/mat-expenses' },
                { label: 'Direct expenses', path: '/dir-expenses' },
                { label: 'Direct expenses by tickets', path: '/dir-expenses-tickets' },
                { label: 'Implementer Finacials', path: '/imp-financials' },
                { label: 'Cash Outflow', path: '/cash-out' },
                { label: 'Reporting', path: '/reporting' },
                { label: 'Project/Ticket payroll audit', path: '/ticket-payroll-audit' },
              ],
            },
            {
              label: 'Data Tools',
              items: [
                { label: 'Expected Balances', path: '/expected-payable-balances' },
                { label: 'Bid Dashboard', path: '/bids' },
                { label: 'Transactions Import', path: '/ingest/transactions' },
                { label: 'Transactions Dashboard', path: '/bookkeeping/transactions' },
              ],
            },
          ],
        },
      ];
    }

    /* ===== IMPLEMENTER ===== */
    if (userType === 'implementer') {
      return [
        ...base,
        {
          type: 'menu',
          key: 'work',
          label: 'Work',
          icon: <AccessTimeRoundedIcon fontSize="small" />,
          items: [
            { label: 'Payrolls', path: '/payrolls' },
            { label: 'Payments', path: '/implementer-payments' },
            { label: 'Advances', path: '/implementer-advances' },
            { label: 'Timesheet', path: '/timesheet' },
          ],
        },
        {
          type: 'menu',
          key: 'insightsTools',
          label: 'Tools',
          icon: <SettingsSuggestRoundedIcon fontSize="small" />,
          items: [
            { label: 'Bid Dashboard', path: '/bids' },
          ],
        },
      ];
    }

    /* ===== MANAGER / GENERAL MANAGER ===== */
    if (userType === 'manager' || userType === 'general manager') {
      return [
        ...base,
        {
          type: 'menu',
          key: 'operations',
          label: 'Operations',
          icon: <ApartmentRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Tickets & Services',
              items: [
                { label: 'Create Ticket', path: '/createTicket' },
                { label: 'Create Project', path: '/createTicket?taskManagerType=project' },
                {label: 'Generate quote', path: '/generateQuote' }
              ],
            },
            {
              label: 'Buildings',
              items: [
                { label: 'Buildings & Profiles', path: '/buildings' },
                { label: 'Maintenance & Assets', path: '/buildings/maintenance' },
                { label: 'Maintenance Calendar', path: '/maintenance/calendar' },
              ],
            },
            {
              label: 'Tenants & Units',
              items: [
                { label: 'Units & Rooms Status', path: '/rental-dashboard' },
                { label: 'Tenants', path: '/tenants' },
                { label: 'Leases', path: '/leases' },
                { label: 'Charges', path: '/charges' },
                { label: 'Payments', path: '/tenant-payments' },
                { label: 'New Move-In', path: '/move-in' },
              ],
            },
          ],
        },
        {
          type: 'menu',
          key: 'financials',
          label: 'Financials',
          icon: <RequestQuoteRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Billing & AR',
              items: [
                { label: 'Invoice Generator', path: '/invoice-generator-mt' },
                { label: 'Recurring Charges', path: '/recurring-charges' },
                { label: 'Invoices', path: '/invoices' },
                { label: 'Invoices Reconciliation', path: '/invoice-reconciliation' },
                { label: 'Accounts Receivable', path: '/receivable-ledger' },
              ],
            },
            {
              label: 'Payroll',
              items: [
                { label: 'Payroll Generator', path: '/tasker-payroll' },
                { label: 'Tasker Payrolls', path: '/tasker-payrolls' },
              ],
            },
            {
              label: 'Expenses & Payables',
              items: [
                { label: 'Business Expenses', path: '/bookkeeping/business-receipts' },
                { label: 'External Payables', path: '/bookkeeping/external-payables' },
                { label: 'Accounts Payable', path: '/tasker-payable-ledger' },
              ],
            },
            {
              label: 'Banking & Reconciliation',
              items: [
                { label: 'Bank Accounts', path: '/bookkeeping/bank-accounts' },
                { label: 'Bank Ledger', path: '/bookkeeping/bank-ledger' },
                { label: 'Bank Reconciliation', path: '/tools/bank-reconciliation' },
              ],
            },
            {
              label: 'Accounting',
              items: [
                { label: 'Chart of Accounts', path: '/accounting/coa' },
                { label: 'Journal Entries', path: '/accounting/journal-entries' },
                { label: 'Category Ledger', path: '/accounting/categories' },
                { label: 'Posting Simulator', path: '/accounting/posting' },
                { label: 'Open/Close accounts', path: '/accounting/open-close-balances' },
                { label: 'Journal explorer', path: '/accounting/journal-explorer' },
                { label: 'Trial Balance', path: '/accounting/trial-balance' },
                { label: 'Profit & Loss', path: '/accounting/pl' },
                { label: 'Balance Sheet', path: '/accounting/balance-sheet' },
                { label: 'Financial Reports', path: '/accounting/financial-reports' },
              ],
            },
          ],
        },
        {
          type: 'menu',
          key: 'insightsTools',
          label: 'Insights & Tools',
          icon: <AssessmentRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Analytics',
              items: [
                { label: 'Gross P&L per Project', path: '/pnl' },
                { label: 'Invoices summary', path: '/invoices-summary' },
                { label: 'Audit of Completed Uninvoiced Tickets', path: '/audit-uninvoiced-tickets' },
                { label: 'Cash Inflow', path: '/cash-in' },
                { label: 'Reporting', path: '/reporting' },
              ],
            },
            {
              label: 'Data Tools',
              items: [
                // ADDED FROM SECOND NAV: Manager / General Manager.
                { label: 'Bid Dashboard', path: '/bids' },
                { label: 'Transactions Import', path: '/ingest/transactions' },
                { label: 'Transactions Dashboard', path: '/bookkeeping/transactions' },
                { label: 'Find Post', path: '/find-post' },
              ],
            },
            {
              label: 'Growth Intelligence',
              items: [
                // ADDED FROM SECOND NAV: Manager / General Manager.
                { label: 'Discovery Engine', path: '/discovery' },
              ],
            },
          ],
        },
      ];
    }

    /* ===== ADMIN ===== */
    if (userType === 'admin') {
      return [
        ...base,
        {
          type: 'menu',
          key: 'operations',
          label: 'Operations',
          icon: <SummarizeRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Tickets & Bids',
              items: [
                { label: 'Create Ticket', path: '/createTicket' },
                { label: 'Create Project', path: '/createTicket?taskManagerType=project' },
                { label: 'Create Tickets', path: '/manual-bulk-tickets' },
                { label: 'Bid Dashboard', path: '/bids' },
                { label: 'New Invitation', path: '/bids/new' },
              ],
            },
          ],
        },
        {
          type: 'menu',
          key: 'financials',
          label: 'Financials',
          icon: <AccountBalanceRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Billing & AR',
              items: [
                { label: 'Recurring Charges', path: '/recurring-charges' },
                { label: 'Invoices', path: '/invoices' },
                { label: 'Accounts Receivable', path: '/receivable-ledger' },
                { label: 'Receipts', path: '/ums/tasker-receipts' },
              ],
            },
            {
              label: 'Payroll',
              items: [
                { label: 'Tasker Payrolls', path: '/tasker-payrolls' },
              ],
            },
            {
              label: 'Expenses & Payables',
              items: [
                { label: 'Business Expenses', path: '/bookkeeping/business-receipts' },
                { label: 'External Payables', path: '/bookkeeping/external-payables' },
                { label: 'Accounts Payable', path: '/tasker-payable-ledger' },
                { label: 'Receipts', path: '/ums/tasker-receipts' },
              ],
            },
            {
              label: 'Banking & Reconciliation',
              items: [
                { label: 'Bank Ledger', path: '/bookkeeping/bank-ledger' },
                { label: 'Bank Reconciliation', path: '/accounting/reconciliation' },
                { label: 'Receipt Reconciliation', path: '/tools/receipt-reconciliation' },
              ],
            },
            {
              label: 'Accounting',
              items: [
                { label: 'Chart of Accounts', path: '/accounting/coa' },
                { label: 'Journal Entries', path: '/accounting/journal-entries' },
                { label: 'Category Ledger', path: '/accounting/categories' },
                { label: 'Posting Simulator', path: '/accounting/posting' },
                { label: 'Open/Close accounts', path: '/accounting/open-close-balances' },
                { label: 'Journal explorer', path: '/accounting/journal-explorer' },
                { label: 'Trial Balance', path: '/accounting/trial-balance' },
                { label: 'Profit & Loss', path: '/accounting/pl' },
                { label: 'Balance Sheet', path: '/accounting/balance-sheet' },
                { label: 'Financial Reports', path: '/accounting/financial-reports' },
              ],
            },
          ],
        },
        {
          type: 'menu',
          key: 'insightsTools',
          label: 'Insights & Tools',
          icon: <AssessmentRoundedIcon fontSize="small" />,
          sections: [
            {
              label: 'Analytics',
              items: [
                { label: 'Gross P&L per Project', path: '/pnl' },
                { label: 'Reporting', path: '/reporting' },
              ],
            },
            {
              label: 'Data Tools',
              items: [
                // ADDED FROM SECOND NAV: Admin.
                { label: 'Expected Balances', path: '/expected-balances' },
                { label: 'Transactions Import', path: '/ingest/transactions' },
                { label: 'Transactions Dashboard', path: '/bookkeeping/transactions' },
              ],
            },
          ],
        },
      ];
    }

    /* ===== CLIENT ===== */
    if (userType === 'client') {
      return [
        ...base,
        {
          type: 'link',
          key: 'createTicket',
          label: 'Request for Service',
          icon: <SummarizeRoundedIcon fontSize="small" />,
          path: '/createTicket',
        },
      ];
    }

    return base;
  }, [userType]);

  const querySensitiveBasePaths = useMemo(() => {
    const paths = roleMenus.flatMap((entry) =>
      entry.type === 'menu' ? getEntryItems(entry).map((item) => item.path) : [entry.path]
    );
    return new Set(
      paths
        .filter((itemPath) => String(itemPath || '').includes('?'))
        .map((itemPath) => normalizeRoutePath(itemPath))
    );
  }, [roleMenus]);

  const isActive = useCallback(
    (target) =>
      routeMatches(
        target,
        location.pathname,
        location.search,
        querySensitiveBasePaths
      ),
    [location.pathname, location.search, querySensitiveBasePaths]
  );

  const isMenuEntryActive = useCallback(
    (entry) => getEntryItems(entry).some((item) => isActive(item.path)),
    [isActive]
  );

  const isGmPanelActive = useMemo(
    () => gmRoutesFiltered.some((item) => isActive(item.path)),
    [gmRoutesFiltered, isActive]
  );

  const desktopBtnSx = {
    color: NAV_TEXT,
    mx: 0.35,
    textTransform: 'none',
    borderRadius: 1.5,
    fontWeight: 650,
    textShadow: NAV_TEXT_SHADOW,
    transition: 'background-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease',
    '&:hover': {
      backgroundColor: NAV_HOVER_BG,
      color: NAV_TEXT,
    },
    '&[aria-current="page"], &[data-active="true"]': {
      backgroundColor: NAV_ACTIVE_BG,
      color: NAV_TEXT,
      boxShadow: `inset 0 -1px 0 ${NAV_ACTIVE_LINE}`,
    },
    '&[aria-current="page"]:hover, &[data-active="true"]:hover': {
      backgroundColor: NAV_OPEN_BG,
    },
    '&.MuiButton-outlined': {
      borderColor: 'rgba(255,255,255,0.54)',
    },
    '&.MuiButton-outlined:hover': {
      borderColor: 'rgba(255,255,255,0.78)',
      backgroundColor: NAV_HOVER_BG,
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.44)',
    },
    whiteSpace: 'nowrap',
    minWidth: compact ? 38 : 0,
    px: compact ? (ultraCompact ? 0.45 : 0.85) : 1.1,
    py: compact ? 0.55 : 0.65,
  };

  const handleSignOut = useCallback(() => {
    try {
      localStorage.removeItem('bypassCreate');
      sessionStorage.removeItem(NAV_HISTORY_KEY);
      try { queryClient.clear?.(); } catch { try { queryClient.removeQueries?.(); } catch {} }
      try { logoutUser?.(); } catch {}
      window.location.replace('/signin');
    } catch {
      window.location.replace('/signin');
    }
  }, [logoutUser, queryClient]);

  const DesktopMenus = () => {
    if (!user) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button component={NavLink} to="/signin" sx={desktopBtnSx} startIcon={!compact ? <LockOpenRoundedIcon /> : null}>
            {compact ? <LockOpenRoundedIcon fontSize="small" /> : 'Sign In'}
          </Button>
          <Button
            component={NavLink}
            to="/signup"
            variant="outlined"
            sx={desktopBtnSx}
            startIcon={!compact ? <PersonAddAlt1RoundedIcon /> : null}
          >
            {compact ? <PersonAddAlt1RoundedIcon fontSize="small" /> : 'Sign Up'}
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 0.25 : 0.5,
          flexWrap: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        {roleMenus.map((entry) => {
          if (entry.type === 'link') {
            return (
              <LinkButton
                key={entry.key}
                to={entry.path}
                label={entry.label}
                icon={entry.icon}
                sx={desktopBtnSx}
                isActive={isActive(entry.path)}
                compact={compact}
              />
            );
          }
          if (entry.type === 'menu') {
            return (
              <DropdownMenu
                key={entry.key}
                id={entry.key}
                label={entry.label}
                icon={entry.icon}
                items={entry.items}
                sections={entry.sections}
                buttonSx={desktopBtnSx}
                compact={compact}
                active={isMenuEntryActive(entry)}
                isItemActive={isActive}
              />
            );
          }
          return null;
        })}
      </Box>
    );
  };

  const RightGroup = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.25 : 0.5 }}>
      {user ? (
        <>
          <Tooltip title={compact ? 'Profile' : ''} arrow disableHoverListener={!compact}>
            <Button
              component={NavLink}
              to="/profile"
              sx={desktopBtnSx}
              aria-current={isActive('/profile') ? 'page' : undefined}
              startIcon={!compact ? <AccountCircleRoundedIcon /> : null}
            >
              {compact ? <AccountCircleRoundedIcon fontSize="small" /> : 'Profile'}
            </Button>
          </Tooltip>
          {!compact && (
            <Typography
              sx={{
                mx: 0.5,
                color: NAV_TEXT,
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: NAV_TEXT_SHADOW,
              }}
            >
              Hi, {user.username}
            </Typography>
          )}
          <Tooltip title={compact ? 'Sign Out' : ''} arrow disableHoverListener={!compact}>
            <Button
              onClick={handleSignOut}
              sx={desktopBtnSx}
              aria-label="Sign out"
              variant="outlined"
              startIcon={!compact ? <LockOpenRoundedIcon /> : null}
            >
              {compact ? <LockOpenRoundedIcon fontSize="small" /> : 'Sign Out'}
            </Button>
          </Tooltip>
        </>
      ) : null}
    </Box>
  );

  const mobileItemSx = {
    py: 1.15,
    px: 2,
    borderLeft: '2px solid transparent',
    transition: 'background-color 140ms ease, border-color 140ms ease',
    '&.Mui-selected': {
      backgroundColor: 'rgba(75,158,241,0.07)',
      borderLeftColor: 'rgba(75,158,241,0.58)',
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'rgba(75,158,241,0.10)',
    },
    '&:hover': {
      backgroundColor: 'rgba(75,158,241,0.05)',
    },
  };
  const toggleGroup = (name) =>
    setOpenGroups((s) => ({ ...s, [name]: !s[name] }));

  const toggleSubGroup = (menuKey, sectionKey) => {
    const id = `${menuKey}:${sectionKey}`;
    setOpenSubGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const MobileDrawer = () => {
    if (!user) {
      const closeDrawer = () => setDrawerOpen(false);
      const publicItems = [
        { label: 'Home', href: '/', icon: <DashboardRoundedIcon fontSize="small" /> },
        { label: 'Field Services', href: '/services', icon: <ApartmentRoundedIcon fontSize="small" /> },
        { label: 'Platform', href: '/platform', icon: <SettingsSuggestRoundedIcon fontSize="small" /> },
        { label: 'Projects', href: '/projects', icon: <ReceiptLongRoundedIcon fontSize="small" /> },
        { label: 'About', href: '/about', icon: <InfoRoundedIcon fontSize="small" /> },
        { label: 'Contact', href: '/contact', icon: <RequestQuoteRoundedIcon fontSize="small" /> },
      ];

      return (
        <Box sx={{ width: 310 }} role="presentation">
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>TaskerSys</Typography>
            <Typography variant="body2" color="text.secondary">
              Property operations platform and NYC field services
            </Typography>
          </Box>
          <List subheader={<ListSubheader>Explore</ListSubheader>}>
            {publicItems.map((item) => (
              <ListItem key={item.href} disablePadding>
                <ListItemButton
                  sx={mobileItemSx}
                  component="a"
                  href={item.href}
                  onClick={closeDrawer}
                  selected={
                    item.href === '/about'
                      ? location.pathname === '/about'
                      : item.href === '/' && location.pathname === '/'
                  }
                >
                  {React.cloneElement(item.icon, { style: { marginRight: 10 } })}
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List subheader={<ListSubheader>Account</ListSubheader>}>
            <ListItem disablePadding>
              <ListItemButton sx={mobileItemSx} component={NavLink} to="/signin" onClick={closeDrawer}>
                <LockOpenRoundedIcon fontSize="small" style={{ marginRight: 10 }} />
                <ListItemText primary="Sign In" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={mobileItemSx} component={NavLink} to="/signup" onClick={closeDrawer}>
                <PersonAddAlt1RoundedIcon fontSize="small" style={{ marginRight: 10 }} />
                <ListItemText primary="Sign Up" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      );
    }

    return (
      <Box sx={{ width: 320 }} role="presentation">
        <List subheader={<ListSubheader>Main</ListSubheader>}>
          {roleMenus.filter((e) => e.type === 'link').map((e) => (
            <ListItem key={e.key} disablePadding>
              <ListItemButton
                sx={mobileItemSx}
                component={NavLink}
                to={e.path}
                selected={isActive(e.path)}
                onClick={() => setDrawerOpen(false)}
              >
                {e.icon}
                <ListItemText primary={<Box sx={{ ml: 1 }}>{e.label}</Box>} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {roleMenus
          .filter((e) => e.type === 'menu')
          .map((e) => {
            const hasSections = Array.isArray(e.sections) && e.sections.length > 0;
            const entryActive = isMenuEntryActive(e);

            return (
              <Box key={e.key}>
                <ListItemButton
                  onClick={() => toggleGroup(e.key)}
                  selected={entryActive}
                  aria-current={entryActive ? 'page' : undefined}
                  sx={{
                    ...mobileItemSx,
                    py: 1.05,
                    ...(openGroups[e.key] && !entryActive
                      ? { backgroundColor: 'rgba(75,158,241,0.045)' }
                      : {}),
                  }}
                >
                  {e.icon}
                  <ListItemText primary={<Box sx={{ ml: 1 }}>{e.label}</Box>} />
                  {openGroups[e.key] ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                </ListItemButton>

                <Collapse in={openGroups[e.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {hasSections
                      ? e.sections.map((section, sectionIndex) => {
                          const sectionKey = `${e.key}:${section.label || sectionIndex}`;
                          const manyItems = (section.items || []).length > 1;

                          if (!section.label) {
                            return (section.items || []).map((it) => {
                              const needsFull = REFRESH_PATHS.includes(it.path);
                              const Comp = needsFull ? 'a' : RouterLink;
                              const props = needsFull ? { href: it.path } : { to: it.path };
                              return (
                                <ListItem key={it.path} disablePadding>
                                  <ListItemButton
                                    sx={{ ...mobileItemSx, pl: 5 }}
                                    component={Comp}
                                    {...props}
                                    selected={isActive(it.path)}
                                    onClick={() => { queueMicrotask(() => setDrawerOpen(false)); }}
                                  >
                                    <ListItemText primary={it.label} />
                                  </ListItemButton>
                                </ListItem>
                              );
                            });
                          }

                          if (!manyItems) {
                            const onlyItem = section.items[0];
                            const needsFull = REFRESH_PATHS.includes(onlyItem.path);
                            const Comp = needsFull ? 'a' : RouterLink;
                            const props = needsFull ? { href: onlyItem.path } : { to: onlyItem.path };

                            return (
                              <ListItem key={sectionKey} disablePadding>
                                <ListItemButton
                                  sx={{
                                    ...mobileItemSx,
                                    pl: 5,
                                    color: 'inherit',
                                    fontWeight: isActive(onlyItem.path) ? 650 : 600,
                                  }}
                                  component={Comp}
                                  {...props}
                                  selected={isActive(onlyItem.path)}
                                  onClick={() => { queueMicrotask(() => setDrawerOpen(false)); }}
                                >
                                  <ListItemText primary={onlyItem.label} />
                                </ListItemButton>
                              </ListItem>
                            );
                          }

                          const isSectionOpen = !!openSubGroups[sectionKey];
                          const sectionActive = (section.items || []).some((item) =>
                            isActive(item.path)
                          );

                          return (
                            <Box key={sectionKey}>
                              <ListItemButton
                                selected={sectionActive}
                                aria-current={sectionActive ? 'page' : undefined}
                                sx={{
                                  ...mobileItemSx,
                                  pl: 5,
                                  color: 'inherit',
                                  fontWeight: sectionActive ? 650 : 600,
                                  ...(isSectionOpen && !sectionActive
                                    ? { backgroundColor: 'rgba(75,158,241,0.035)' }
                                    : {}),
                                }}
                                onClick={() =>
                                  toggleSubGroup(e.key, section.label || String(sectionIndex))
                                }
                              >
                                <ListItemText primary={section.label} />
                                {isSectionOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                              </ListItemButton>
                              <Collapse in={isSectionOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {section.items.map((it) => {
                                    const needsFull = REFRESH_PATHS.includes(it.path);
                                    const Comp = needsFull ? 'a' : RouterLink;
                                    const props = needsFull ? { href: it.path } : { to: it.path };
                                    return (
                                      <ListItem key={it.path} disablePadding>
                                        <ListItemButton
                                          sx={{ ...mobileItemSx, pl: 7 }}
                                          component={Comp}
                                          {...props}
                                          selected={isActive(it.path)}
                                          onClick={() => { queueMicrotask(() => setDrawerOpen(false)); }}
                                        >
                                          <ListItemText primary={it.label} />
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            </Box>
                          );
                        })
                      : e.items?.map((it) => {
                          const needsFull = REFRESH_PATHS.includes(it.path);
                          const Comp = needsFull ? 'a' : RouterLink;
                          const props = needsFull ? { href: it.path } : { to: it.path };
                          return (
                            <ListItem key={it.path} disablePadding>
                              <ListItemButton
                                sx={{ ...mobileItemSx, pl: 5 }}
                                component={Comp}
                                {...props}
                                selected={isActive(it.path)}
                                onClick={() => { queueMicrotask(() => setDrawerOpen(false)); }}
                              >
                                <ListItemText primary={it.label} />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                  </List>
                </Collapse>
                <Divider />
              </Box>
            );
          })}

        {(userType === 'general manager' || userType === 'admin') && (
          <>
            <List subheader={<ListSubheader>General Manager Panel</ListSubheader>}>
              <ListItemButton
                onClick={() => toggleGroup('GMPanel')}
                selected={isGmPanelActive}
                aria-current={isGmPanelActive ? 'page' : undefined}
                sx={{
                  ...mobileItemSx,
                  py: 1.05,
                  ...(openGroups.GMPanel && !isGmPanelActive
                    ? { backgroundColor: 'rgba(75,158,241,0.045)' }
                    : {}),
                }}
              >
                <AdminPanelSettingsRoundedIcon fontSize="small" />
                <ListItemText primary={<Box sx={{ ml: 1 }}>General Manager Panel</Box>} />
                {openGroups.GMPanel ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
              </ListItemButton>
              <Collapse in={openGroups.GMPanel} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {gmRoutesFiltered.map((it) => {
                    const needsFull = REFRESH_PATHS.includes(it.path);
                    const Comp = needsFull ? 'a' : RouterLink;
                    const props = needsFull ? { href: it.path } : { to: it.path };
                    return (
                      <ListItem key={it.path} disablePadding>
                        <ListItemButton
                          sx={{ ...mobileItemSx, pl: 5 }}
                          component={Comp}
                          {...props}
                          selected={isActive(it.path)}
                          onClick={() => queueMicrotask(() => setDrawerOpen(false))}
                        >
                          <ListItemText primary={it.label} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </List>
            <Divider />
          </>
        )}

        <Divider />
        <List subheader={<ListSubheader>Account</ListSubheader>}>
          <ListItem disablePadding>
            <ListItemButton
              sx={mobileItemSx}
              component={NavLink}
              to="/profile"
              onClick={() => setDrawerOpen(false)}
              selected={isActive('/profile')}
            >
              <AccountCircleRoundedIcon fontSize="small" style={{ marginRight: 10 }} />
              <ListItemText primary="Profile" />
            </ListItemButton>
          </ListItem>

          {hasActiveSubscription && (
            <ListItem disablePadding>
              <ListItemButton
                sx={mobileItemSx}
                component={NavLink}
                to="/profile/edit"
                onClick={() => setDrawerOpen(false)}
                selected={isActive('/profile/edit')}
              >
                <EditRoundedIcon fontSize="small" style={{ marginRight: 10 }} />
                <ListItemText primary="Edit Profile" />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem disablePadding>
            <ListItemButton
              sx={mobileItemSx}
              onClick={() => {
                setDrawerOpen(false);
                handleSignOut();
              }}
            >
              <ListItemText primary="Sign Out" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    );
  };

  useEffect(() => {
    if (user) return;
    if (!isPubliclyAccessiblePath(location.pathname)) {
      window.location.replace('/signin');
    }
  }, [user, location.pathname]);

  const PublicHeader = () => {
    const publicLinks = PUBLIC_NAVIGATION.map((item) => ({
      label: item.label,
      to: item.path,
    }));
    const publicDashboardPath = dashboardPathForUser(user);

    const isCurrentPublicLink = (to) => {
      if (to === '/services') return location.pathname === '/services' || location.pathname.startsWith('/services/');
      return location.pathname === to;
    };

    return (
      <>
        <div className="ts-public-topline">
          <div className="ts-public-topline__inner">
            <span>Property services. Connected operations.</span>
            <a href="tel:+17183742603">718-374-2603</a>
          </div>
        </div>
        <header className="ts-public-header">
          <div className="ts-public-header__inner">
            <NavLink className="ts-public-brand" to="/" aria-label="TaskerSys home">
              <span>Tasker<span>Sys</span></span>
            </NavLink>

            <nav className="ts-public-nav" aria-label="Primary navigation">
              {publicLinks.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-current={isCurrentPublicLink(item.to) ? 'page' : undefined}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="ts-public-account">
              {user ? (
                <>
                  <NavLink className="ts-public-account__link" to="/profile">Profile</NavLink>
                  <NavLink className="ts-button ts-button--small" to={publicDashboardPath}>Open workspace</NavLink>
                </>
              ) : (
                <>
                  <NavLink className="ts-public-account__link" to="/signin">Sign in</NavLink>
                  <NavLink className="ts-button ts-button--small" to="/contact">Request a quote</NavLink>
                </>
              )}
              <IconButton
                className="ts-public-mobile-button"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open public navigation"
              >
                <MenuIcon />
              </IconButton>
            </div>
          </div>
        </header>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } }}
        >
          <div className="ts-public-mobile-drawer">
            <div className="ts-public-mobile-drawer__brand">
              <NavLink className="ts-public-brand" to="/" onClick={() => setDrawerOpen(false)}>
                <span>Tasker<span>Sys</span></span>
              </NavLink>
              <p>Property operations platform and NYC field services.</p>
            </div>
            <nav className="ts-public-mobile-drawer__links" aria-label="Mobile public navigation">
              <NavLink to="/" onClick={() => setDrawerOpen(false)} aria-current={location.pathname === '/' ? 'page' : undefined}>Home</NavLink>
              {publicLinks.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  aria-current={isCurrentPublicLink(item.to) ? 'page' : undefined}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="ts-public-mobile-drawer__account">
              {user ? (
                <>
                  <NavLink className="ts-button ts-button--outline" to="/profile" onClick={() => setDrawerOpen(false)}>Profile</NavLink>
                  <NavLink className="ts-button ts-button--primary" to={publicDashboardPath} onClick={() => setDrawerOpen(false)}>Open workspace</NavLink>
                </>
              ) : (
                <>
                  <NavLink className="ts-button ts-button--outline" to="/signin" onClick={() => setDrawerOpen(false)}>Sign in</NavLink>
                  <NavLink className="ts-button ts-button--primary" to="/contact" onClick={() => setDrawerOpen(false)}>Request a quote</NavLink>
                  <NavLink className="ts-public-account__link" to="/signup" onClick={() => setDrawerOpen(false)}>Create an account</NavLink>
                </>
              )}
            </div>
          </div>
        </Drawer>
      </>
    );
  };

  if (isPublicShell) {
    return <PublicHeader />;
  }

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: BRAND_BLUE,
          backgroundImage: 'none',
          color: NAV_TEXT,
          borderBottom: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 1px 6px rgba(15,23,42,0.09)',
        }}
      >
        <Toolbar sx={{ gap: 0.5, minHeight: 56 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 0.5 }}>
            <Tooltip title="Back (Alt+← or ⌘[)">
              <span>
                <IconButton
                  edge="start"
                  color="inherit"
                  size="small"
                  onClick={handleBack}
                  disabled={!canGoBack}
                  aria-label="Go back"
                  sx={{
                    p: 0.4,
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    color: NAV_TEXT,
                    textShadow: NAV_TEXT_SHADOW,
                    '&:hover': { backgroundColor: NAV_HOVER_BG },
                  }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Forward (Alt+→ or ⌘])">
              <span>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleForward}
                  disabled={!canGoForward}
                  aria-label="Go forward"
                  sx={{
                    p: 0.4,
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    color: NAV_TEXT,
                    textShadow: NAV_TEXT_SHADOW,
                    '&:hover': { backgroundColor: NAV_HOVER_BG },
                  }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, gap: 1.25 }}>
            <NavLink
              to={user ? (userType === 'general manager' ? '/ums/general-manager-dashboard' : '/dashboard') : '/'}
              aria-label={user ? 'Open TaskerSys dashboard' : 'TaskerSys homepage'}
              style={{
                color: NAV_TEXT,
                textDecoration: 'none',
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: 0.2,
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                textShadow: NAV_TEXT_SHADOW,
              }}
            >
              TaskerSys
            </NavLink>

            {(userType === 'general manager' || userType === 'admin') && !isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', minWidth: 0 }}>
                {(() => {
                  const match = GM_ROUTES.find((r) => location.pathname.startsWith(r.path));
                  return match ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.92)',
                        lineHeight: 1,
                        mb: 0.3,
                        whiteSpace: 'nowrap',
                        textShadow: NAV_TEXT_SHADOW,
                      }}
                    >
                      {match.label}
                    </Typography>
                  ) : null;
                })()}

                <DropdownMenu
                  id="gm-panel"
                  label="General Manager Panel"
                  icon={<AdminPanelSettingsRoundedIcon />}
                  items={GM_ROUTES.filter((r) => !r.adminOnly || userType === 'admin')}
                  buttonSx={{
                    ...desktopBtnSx,
                    alignSelf: 'flex-start',
                    minHeight: 32,
                    mx: 0,
                  }}
                  compact={compact}
                  active={isGmPanelActive}
                  isItemActive={isActive}
                />
              </Box>
            )}
          </Box>

          {!isMobile ? (
            <>
              <DesktopMenus />
              <RightGroup />
            </>
          ) : (
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                color: NAV_TEXT,
                textShadow: NAV_TEXT_SHADOW,
                '&:hover': { backgroundColor: NAV_HOVER_BG },
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { borderTopRightRadius: 10, borderBottomRightRadius: 10 } }}
      >
        <Box sx={{ width: 320 }} role="presentation">
          <MobileDrawer />
        </Box>
      </Drawer>

      <Toolbar />
    </>
  );
};

export default Nav;

